/**
 * UnitIdentifierModal — IMEI / Serial / Unit Identifier management
 *
 * Data model (per user requirement):
 *   Product → ProductVariant → StockUnit/SerializedUnit → UnitIdentifier (IMEI, Serial, VIN...)
 *
 * Each physical unit in the Qty gets its own row with configurable identifier types.
 * Per-unit images stored via ImageManagerModal → imageStorage.js
 *
 * Storage key: proc_units_{itemCode}
 * Value: [{ unitNo, identifiers:{IMEI, Serial, Barcode...}, imageKey, imageSrc }]
 *
 * Opens from:
 *   - ProcItemGrid ⋮ → "Split Barcode / IMEI Details"
 *   - NewItemModal "🔢 IMEI / Serial (for Qty)" button
 *   - F10 SizeConfigModal
 */
import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import ImageManagerModal from './ImageManagerModal';
import { loadImages, saveImages } from '../../lib/imageStorage';

const STORAGE_PREFIX = 'proc_units_';

// ─── Predefined identifier types ─────────────────────────────────────────
const PRESET_TYPES = [
  { id: 'imei',     label: 'IMEI',          desc: 'Mobile device IMEI number' },
  { id: 'imei2',    label: 'IMEI 2',         desc: 'Secondary IMEI (dual SIM)' },
  { id: 'serial',   label: 'Serial No',      desc: 'Manufacturer serial number' },
  { id: 'barcode',  label: 'Barcode',        desc: 'Printed barcode / OEM code' },
  { id: 'vin',      label: 'VIN',            desc: 'Vehicle Identification Number' },
  { id: 'assetTag', label: 'Asset Tag',      desc: 'Fixed asset tag number' },
  { id: 'oemPart',  label: 'OEM Part No',    desc: 'OEM / manufacturer part number' },
  { id: 'motorNo',  label: 'Motor No',       desc: 'Motor / engine number' },
  { id: 'lotNo',    label: 'Lot No',         desc: 'Production lot number' },
  { id: 'batchNo',  label: 'Batch No',       desc: 'Manufacturing batch' },
  { id: 'mfgDate',  label: 'Mfg Date',       desc: 'Manufacturing date' },
  { id: 'expDate',  label: 'Expiry Date',    desc: 'Expiry date' },
];

// ─── Load/save units from localStorage ────────────────────────────────────
function loadUnits(itemCode) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + itemCode);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function saveUnits(itemCode, data) {
  localStorage.setItem(STORAGE_PREFIX + itemCode, JSON.stringify(data));
}

// ─── Main Modal ─────────────────────────────────────────────────────────────
export default function UnitIdentifierModal({ row, onSave, onClose }) {
  const itemCode = row?.code || `item_${row?._id || Date.now()}`;
  const itemName = row?.name || '—';
  const qty      = Math.max(1, parseInt(row?.qty) || 1);

  // Active identifier columns
  const [activeTypes, setActiveTypes] = useState(() => {
    const saved = loadUnits(itemCode);
    return saved?.activeTypes || ['imei', 'serial', 'barcode'];
  });
  const [showTypeMenu, setShowTypeMenu] = useState(false);
  const [customTypeLabel, setCustomTypeLabel] = useState('');

  // Units data: one row per physical unit
  const [units, setUnits] = useState(() => {
    const saved = loadUnits(itemCode);
    if (saved?.units?.length) return saved.units;
    // Initialize from qty
    return Array.from({ length: qty }, (_, i) => ({
      unitNo:      i + 1,
      identifiers: {},
      imageKey:    null,
      imageSrc:    null,
    }));
  });

  // Image manager state
  const [imgTarget, setImgTarget] = useState(null); // { unitIdx }

  // ── Helpers ──
  function setUnitField(unitIdx, typeId, value) {
    setUnits(u => u.map((r, i) => i === unitIdx ? { ...r, identifiers: { ...r.identifiers, [typeId]: value } } : r));
  }

  function addUnit() {
    setUnits(u => [...u, { unitNo: u.length + 1, identifiers: {}, imageKey: null, imageSrc: null }]);
  }

  function removeUnit(idx) {
    setUnits(u => u.filter((_, i) => i !== idx).map((r, i) => ({ ...r, unitNo: i + 1 })));
  }

  function addIdentifierType(typeId) {
    if (!activeTypes.includes(typeId)) setActiveTypes(t => [...t, typeId]);
    setShowTypeMenu(false);
  }

  function addCustomType() {
    const lbl = customTypeLabel.trim();
    if (!lbl) return;
    const id = `custom_${lbl.toLowerCase().replace(/\s+/g, '_')}`;
    if (!PRESET_TYPES.find(t => t.id === id)) {
      PRESET_TYPES.push({ id, label: lbl, desc: 'Custom identifier' });
    }
    addIdentifierType(id);
    setCustomTypeLabel('');
  }

  function removeIdentifierType(typeId) {
    setActiveTypes(t => t.filter(x => x !== typeId));
  }

  function autoGenerate(typeId) {
    const base = row?.code || 'ITEM';
    setUnits(u => u.map((r, i) => ({
      ...r,
      identifiers: {
        ...r.identifiers,
        [typeId]: typeId === 'imei'   ? `35400000${String(10000 + i).padStart(5, '0')}`
                : typeId === 'serial' ? `SN${base}${String(i + 1).padStart(4, '0')}`
                : typeId === 'barcode'? `${base}-${String(i + 1).padStart(3, '0')}`
                : `${typeId.toUpperCase()}-${String(i + 1).padStart(4, '0')}`,
      },
    })));
  }

  function handleImageSave(ref, unitIdx) {
    const imgs = loadImages(ref.imageKey.replace('proc_img_', ''));
    setUnits(u => u.map((r, i) => i === unitIdx
      ? { ...r, imageKey: ref.imageKey, imageSrc: imgs[0]?.thumb || null }
      : r
    ));
    setImgTarget(null);
  }

  function handleSave() {
    const data = { activeTypes, units, itemCode, itemName, savedAt: Date.now() };
    saveUnits(itemCode, data);
    onSave && onSave(data);
    onClose();
  }

  const typeMap = Object.fromEntries(PRESET_TYPES.map(t => [t.id, t]));
  const filledCount = units.filter(u => Object.values(u.identifiers).some(v => v?.trim())).length;

  return (
    <>
      <div className="pi2-overlay-bg" onClick={onClose} />
      <div className="pi2-modal-wrap">
        <div className="pi2-modal" style={{ width: 960, maxWidth: '97vw' }}>

          {/* Header */}
          <div className="pi2-modal-head">
            <div className="t">
              <small>IMEI / Serial / Unit Identifiers · {itemCode}</small>
              {itemName} — {qty} unit{qty > 1 ? 's' : ''}
            </div>
            <button className="x" onClick={onClose}>✕</button>
          </div>

          {/* Toast */}
          <div className="pi2-modal-toast">
            ℹ️ Each physical unit gets its own identifiers (IMEI, Serial, Barcode etc.) and optionally an image.
            Supports Samsung phone: IMEI1 + IMEI2 + Serial. Industrial motor: Serial + OEM Part + Motor No.
          </div>

          {/* Identifier type config bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderBottom: '1px solid var(--pi2-border)', background: 'var(--pi2-surface-alt)', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--pi2-ink-soft)', whiteSpace: 'nowrap' }}>IDENTIFIER TYPES:</span>
            {activeTypes.map(typeId => {
              const t = typeMap[typeId] || { id: typeId, label: typeId };
              return (
                <span key={typeId} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#dbeafe', color: '#1d4ed8', border: '1px solid #93c5fd', borderRadius: 12, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                  {t.label}
                  <button onClick={() => removeIdentifierType(typeId)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#1d4ed8', fontSize: 12, padding: 0, lineHeight: 1 }}>✕</button>
                </span>
              );
            })}
            {/* Add type button */}
            <div style={{ position: 'relative' }}>
              <button className="pi2-addfld" type="button" onClick={() => setShowTypeMenu(m => !m)}>
                <span>+</span> Add Identifier Type
              </button>
              {showTypeMenu && ReactDOM.createPortal(
                <>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 9997 }} onClick={() => setShowTypeMenu(false)} />
                  <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 9998, background: 'var(--pi2-surface)', border: '1px solid var(--pi2-border)', borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,.18)', padding: 16, width: 380 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>Add Identifier Type</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, maxHeight: 280, overflowY: 'auto' }}>
                      {PRESET_TYPES.filter(t => !activeTypes.includes(t.id)).map(t => (
                        <button key={t.id} type="button" onClick={() => addIdentifierType(t.id)}
                          style={{ textAlign: 'left', padding: '6px 10px', border: '1px solid var(--pi2-border)', borderRadius: 4, cursor: 'pointer', background: 'var(--pi2-surface)' }}>
                          <div style={{ fontWeight: 700, fontSize: 12 }}>{t.label}</div>
                          <div style={{ fontSize: 10, color: 'var(--pi2-ink-soft)' }}>{t.desc}</div>
                        </button>
                      ))}
                    </div>
                    <div style={{ marginTop: 10, display: 'flex', gap: 6 }}>
                      <input value={customTypeLabel} onChange={e => setCustomTypeLabel(e.target.value)}
                        placeholder="Custom identifier name…" style={{ flex: 1, padding: '4px 8px', border: '1px solid var(--pi2-border)', borderRadius: 3, fontSize: 12 }}
                        onKeyDown={e => e.key === 'Enter' && addCustomType()} />
                      <button className="pi2-btn primary" type="button" onClick={addCustomType}>+ Add</button>
                    </div>
                  </div>
                </>,
                document.body
              )}
            </div>
          </div>

          {/* Units grid */}
          <div style={{ overflow: 'auto', maxHeight: 440 }}>
            <table className="pi2-table" style={{ width: '100%', tableLayout: 'auto' }} data-testid="unit-identifier-table">
              <thead>
                <tr>
                  <th style={{ width: 50, textAlign: 'center' }}>Unit</th>
                  {activeTypes.map(typeId => (
                    <th key={typeId} style={{ minWidth: 140 }}>
                      {typeMap[typeId]?.label || typeId}
                      <button title="Auto-fill this column" type="button"
                        onClick={() => autoGenerate(typeId)}
                        style={{ marginLeft: 4, border: 'none', background: 'none', cursor: 'pointer', fontSize: 10, color: 'rgba(255,255,255,.7)' }}>⚙</button>
                    </th>
                  ))}
                  <th style={{ width: 52, textAlign: 'center' }}>Image</th>
                  <th style={{ width: 36 }}></th>
                </tr>
              </thead>
              <tbody>
                {units.map((unit, ui) => (
                  <tr key={ui} data-testid={`unit-row-${ui}`}>
                    <td style={{ textAlign: 'center', fontWeight: 700, color: 'var(--pi2-ink-soft)', background: 'var(--pi2-surface-alt)' }}>{unit.unitNo}</td>
                    {activeTypes.map(typeId => (
                      <td key={typeId}>
                        <input className="pi2-cell-edit" style={{ width: '100%', height: 30, padding: '0 6px' }}
                          value={unit.identifiers[typeId] || ''}
                          placeholder={typeMap[typeId]?.label || typeId}
                          onChange={e => setUnitField(ui, typeId, e.target.value)}
                          data-testid={`unit-${ui}-${typeId}`} />
                      </td>
                    ))}
                    <td style={{ textAlign: 'center', padding: 2 }}>
                      <button type="button" title="Attach image for this unit"
                        onClick={() => setImgTarget(ui)}
                        style={{ border: '1px dashed var(--pi2-border)', borderRadius: 3, background: 'none', cursor: 'pointer', width: 36, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
                        {unit.imageSrc
                          ? <img src={unit.imageSrc} alt="" style={{ width: 28, height: 26, objectFit: 'cover', borderRadius: 2 }} />
                          : <span style={{ fontSize: 14 }}>📷</span>
                        }
                      </button>
                    </td>
                    <td>
                      <button type="button" onClick={() => removeUnit(ui)}
                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#c0392b', fontSize: 14, padding: '0 4px' }}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Action bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderTop: '1px solid var(--pi2-border)', background: 'var(--pi2-surface-alt)' }}>
            <button className="pi2-btn" type="button" onClick={addUnit} data-testid="add-unit-btn">
              + Add Unit
            </button>
            <button className="pi2-btn" type="button" onClick={() => setUnits(u => u.map(r => ({ ...r, identifiers: {}, imageKey: null, imageSrc: null })))}>
              Clear All
            </button>
            <span style={{ fontSize: 11, color: 'var(--pi2-ink-soft)', marginLeft: 8 }}>
              {filledCount}/{units.length} units filled · {activeTypes.length} identifier types
            </span>
          </div>

          {/* Footer */}
          <div className="pi2-modal-foot">
            <div style={{ fontSize: 11, color: 'var(--pi2-ink-soft)' }}>
              Stored in localStorage · Key: {STORAGE_PREFIX}{itemCode}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="pi2-btn" type="button" onClick={onClose}>Cancel</button>
              <button className="pi2-btn primary" type="button" onClick={handleSave} data-testid="unit-save-btn">
                Save Unit Data ({units.length} units)
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Per-unit Image Manager */}
      {imgTarget !== null && (
        <ImageManagerModal
          itemKey={`${itemCode}_unit_${imgTarget + 1}`}
          itemLabel={`Unit ${imgTarget + 1} · ${itemName}`}
          onSave={(ref) => handleImageSave(ref, imgTarget)}
          onClose={() => setImgTarget(null)}
        />
      )}
    </>
  );
}
